import React from "react";

export interface InputTextProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  strict?: RegExp;
  mask?: string;
}

export default function InputText({
  value,
  onChange,
  className = "",
  strict,
  mask,
  ...props
}: InputTextProps) {
  const base =
    "w-full px-4 py-3 border border-[#C3CBDE] rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-[#949ABF] outline-none transition-all";

  const applyMask = (inputValue: string, maskPattern: string): string => {
    // Remove all non-digit characters from input
    const digits = inputValue.replace(/\D/g, "");

    let maskedValue = "";
    let digitIndex = 0;

    for (let i = 0; i < maskPattern.length; i++) {
      if (maskPattern[i] === "9") {
        // '9' represents a digit placeholder
        if (digitIndex < digits.length) {
          maskedValue += digits[digitIndex];
          digitIndex++;
        } else {
          // No more digits to add, stop here
          break;
        }
      } else {
        // Add mask character only if we have digits after this position
        // This prevents adding separators when deleting
        if (digitIndex < digits.length) {
          maskedValue += maskPattern[i];
        }
      }
    }

    return maskedValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Apply strict validation first (on raw input)
    if (strict) {
      // Extract only the characters that should be allowed by the strict regex
      const allowedChars = newValue
        .split("")
        .filter((char) => strict.test(char))
        .join("");
      newValue = allowedChars;
    }

    // Apply mask if provided
    if (mask) {
      newValue = applyMask(newValue, mask);
    }

    // Always call onChange with the processed value
    const processedEvent = {
      ...e,
      target: {
        ...e.target,
        value: newValue,
      },
    };
    onChange(processedEvent);
  };

  return (
    <input
      {...props}
      value={value}
      onChange={handleChange}
      className={[base, className].join(" ")}
    />
  );
}
